import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Button,
  Box,
  Typography,
} from "@mui/material";
import React from "react";
import { StopType } from "./types";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import { FieldArray } from "formik";
import {
  commonButtonProps,
  commonTextFieldProps,
  smallButtonProp,
  tableCellStyles,
  tableHeaderStyles,
} from "../../../utils/styles";

interface StopsTableProps {
  stops: StopType[];
  routeId: string;
  isEditing: boolean;
  index: number;
  setFieldValue: (field: string, value: any) => void;
  values: any;
  handleChange: (e: React.ChangeEvent<any>) => void;
  showTime?: boolean;
  hideAddButton?: boolean;
  hideActionButtons?: boolean;
  disabled?: boolean;
  disableStopTime?: boolean;
  errors?: any;
  touched?: any;
}

const StopsTable = ({
  stops,
  routeId,
  isEditing,
  index,
  setFieldValue,
  values,
  handleChange,
  showTime = true,
  hideAddButton = false,
  hideActionButtons = false,
  disabled = false,
  disableStopTime = false,
  errors,
  touched,
}: StopsTableProps) => {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...tableHeaderStyles }}>Stop Number</TableCell>
            <TableCell sx={{ ...tableHeaderStyles }}>Stop Name</TableCell>
            {showTime && (
              <TableCell sx={{ ...tableHeaderStyles }}>Stop Time</TableCell>
            )}
            <TableCell sx={{ ...tableHeaderStyles }}>Contact Person</TableCell>
            <TableCell sx={{ ...tableHeaderStyles }}>Contact Number</TableCell>
            {isEditing && !routeId && !hideActionButtons && (
              <TableCell sx={{ ...tableHeaderStyles }} align="center">
                Actions
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          <FieldArray name={`routes.${index}.stops`}>
            {({ push, insert, remove }) => {
              const handleAddStop = () => {
                const stops = values.routes[index].stops;

                if (stops.length === 0) {
                  // First-time setup: insert fromLocation and toLocation
                  push({
                    id: null,
                    stopNumber: 1,
                    stopName: values.routes[index].fromLocation,
                    routeId,
                    contactPersonName: "",
                    contactPersonNumber: "",
                    stopTime: "",
                    isNew: true,
                    isEdited: true,
                    isDeleted: false,
                  });

                  push({
                    id: null,
                    stopNumber: 2,
                    stopName: values.routes[index].toLocation,
                    routeId,
                    contactPersonName: "",
                    contactPersonNumber: "",
                    stopTime: "",
                    isNew: true,
                    isEdited: true,
                    isDeleted: false,
                  });
                } else {
                  // set the stop number of the last stop to the length of the stops array
                  setFieldValue(
                    `routes.${index}.stops.${stops.length - 1}.stopNumber`,
                    stops.length + 1
                  );
                  // set isEdited to true for the last stop
                  setFieldValue(
                    `routes.${index}.stops.${stops.length - 1}.isEdited`,
                    true
                  );
                  // Insert before last stop
                  insert(stops.length - 1, {
                    id: null,
                    stopNumber: stops.length,
                    stopName: "",
                    routeId,
                    contactPersonName: "",
                    contactPersonNumber: "",
                    stopTime: "",
                    isNew: true,
                    isEdited: true,
                    isDeleted: false,
                  });
                }
              };

              const handleDeleteStop = (stopIndex: number) => {
                const stops = values.routes[index].stops;

                if (stopIndex === 0 || stopIndex === stops.length - 1) return; // Don't delete first/last

                if (stops[stopIndex].id === null) {
                  remove(stopIndex);

                  // Recalculate stop numbers after deletion
                  for (let i = stopIndex; i < stops.length - 1; i++) {
                    setFieldValue(
                      `routes.${index}.stops.${i}.stopNumber`,
                      i + 1
                    );
                  }
                } else {
                  setFieldValue(
                    `routes.${index}.stops.${stopIndex}.isDeleted`,
                    true
                  );
                }
              };

              return (
                <>
                  {values.routes[index].stops
                    .filter((stop: any) => !stop.isDeleted)
                    .map((stop: any, stopIndex: number) => (
                      <TableRow key={stop.id || stopIndex}>
                        <TableCell sx={tableCellStyles}>
                          {stop.stopNumber}
                        </TableCell>
                        <TableCell sx={tableCellStyles}>
                          {isEditing ? (
                            <TextField
                              {...commonTextFieldProps}
                              fullWidth
                              name={`routes.${index}.stops.${stopIndex}.stopName`}
                              label="Stop Name"
                              value={stop.stopName}
                              onChange={(e) => {
                                handleChange(e);
                                setFieldValue(
                                  `routes.${index}.stops.${stopIndex}.isEdited`,
                                  true
                                );
                              }}
                              required
                              disabled={disabled}
                            />
                          ) : (
                            stop.stopName
                          )}
                        </TableCell>
                        {showTime && (
                          <TableCell sx={tableCellStyles}>
                            {isEditing ? (
                              <TextField
                                {...commonTextFieldProps}
                                fullWidth
                                name={`routes.${index}.stops.${stopIndex}.stopTime`}
                                label="Stop Time"
                                type="time"
                                value={stop.stopTime || ""}
                                onChange={(e) => {
                                  handleChange(e);
                                  setFieldValue(
                                    `routes.${index}.stops.${stopIndex}.isEdited`,
                                    true
                                  );
                                }}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                disabled={disableStopTime}
                                error={
                                  Boolean(
                                    touched?.routes?.[index]?.stops?.[stopIndex]
                                      ?.stopTime
                                  ) &&
                                  errors?.routes?.[index]?.stops?.[stopIndex]
                                    ?.stopTime
                                }
                                helperText={
                                  Boolean(
                                    touched?.routes?.[index]?.stops?.[stopIndex]
                                      ?.stopTime
                                  ) &&
                                  errors?.routes?.[index]?.stops?.[stopIndex]
                                    ?.stopTime
                                }
                              />
                            ) : (
                              stop.stopTime || "-"
                            )}
                          </TableCell>
                        )}
                        <TableCell sx={tableCellStyles}>
                          {isEditing ? (
                            <TextField
                              {...commonTextFieldProps}
                              fullWidth
                              name={`routes.${index}.stops.${stopIndex}.contactPersonName`}
                              label="Contact Person"
                              value={stop.contactPersonName || ""}
                              onChange={(e) => {
                                handleChange(e);
                                setFieldValue(
                                  `routes.${index}.stops.${stopIndex}.isEdited`,
                                  true
                                );
                              }}
                              disabled={disabled}
                            />
                          ) : (
                            stop.contactPersonName || "-"
                          )}
                        </TableCell>
                        <TableCell sx={tableCellStyles}>
                          {isEditing ? (
                            <TextField
                              {...commonTextFieldProps}
                              fullWidth
                              name={`routes.${index}.stops.${stopIndex}.contactPersonNumber`}
                              label="Contact Number"
                              value={stop.contactPersonNumber || ""}
                              onChange={(e) => {
                                handleChange(e);
                                setFieldValue(
                                  `routes.${index}.stops.${stopIndex}.isEdited`,
                                  true
                                );
                              }}
                              inputProps={{
                                maxLength: 10,
                                pattern: "[0-9]*",
                              }}
                              disabled={disabled}
                            />
                          ) : (
                            stop.contactPersonNumber || "-"
                          )}
                        </TableCell>

                        {isEditing && !routeId && !hideActionButtons && (
                          <TableCell sx={tableCellStyles} align="center">
                            <Button
                              onClick={() => handleDeleteStop(stopIndex)}
                              variant="outlined"
                              color="error"
                              {...smallButtonProp}
                              disabled={
                                stop.stopNumber === 1 ||
                                stop.stopNumber ===
                                  values.routes[index].stops.length
                              }
                            >
                              Delete
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  {showTime && !hideAddButton && (
                    <TableRow>
                      <TableCell
                        colSpan={12}
                        align="center"
                        sx={tableCellStyles}
                      >
                        <Button
                          startIcon={<AddIcon sx={{ fontSize: "0.7rem" }} />}
                          onClick={handleAddStop}
                          {...commonButtonProps}
                          sx={{
                            fontSize: "0.7rem",
                          }}
                          disabled={
                            values.routes[index].fromLocation === "" ||
                            values.routes[index].toLocation === ""
                          }
                        >
                          Add Stop
                        </Button>
                        {stops.length === 0 && (
                          <Box
                            sx={{
                              mt: 1,
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontSize: "0.75rem",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                color: "grey",
                              }}
                            >
                              Add stops to proceed further.
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </>
              );
            }}
          </FieldArray>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default StopsTable;
